// ═══════════════════════════════════════════════════════════════════════════════
// Map API error codes + payload to localized user-facing messages (helpers / employers)
// ═══════════════════════════════════════════════════════════════════════════════

import { ApiClientError } from '../lib/api';
import type { MessageParams } from './uiMessages';

type Translate = (key: string, params?: MessageParams) => string;

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined;

/**
 * Returns a localized explanation when the server sent a known `code`; otherwise null
 * so the caller can fall back to `err.message` (English from API).
 */
export const mapApiClientErrorToMessage = (err: unknown, t: Translate): string | null => {
  if (!(err instanceof ApiClientError)) {
    return null;
  }

  const { code, body } = err;

  if (code === 'SETTLEMENT_LOCKED') {
    const receiptDate = str(body.receiptDate);
    const start = str(body.lockedPeriodStart);
    const end = str(body.lockedPeriodEnd);
    if (receiptDate && start && end) {
      return t('apiError.settlementLockedReceipt', { receiptDate, start, end });
    }
  }

  if (code === 'SETTLEMENT_RANGE_OVERLAPS_LOCKED') {
    const reqStart = str(body.requestedStartDate);
    const reqEnd = str(body.requestedEndDate);
    const start = str(body.lockedPeriodStart);
    const end = str(body.lockedPeriodEnd);
    if (reqStart && reqEnd && start && end) {
      return t('apiError.settlementRangeOverlapsLocked', {
        reqStart,
        reqEnd,
        start,
        end,
      });
    }
  }

  if (code === 'SETTLEMENT_NO_RECEIPTS_ACK_REQUIRED') {
    return t('apiError.settlementNoReceiptsAck');
  }

  if (code === 'BUDGET_OVERLAPS_SETTLEMENT') {
    return t('apiError.budgetOverlapsSettlement');
  }

  if (code === 'BUDGET_SHRINK_RECEIPTS_OUTSIDE') {
    return t('apiError.budgetShrinkReceiptsOutside');
  }

  if (code === 'BUDGET_DELETE_LOCKED') {
    return t('apiError.budgetDeleteLocked');
  }

  if (code === 'MAIN_BUDGET_OVERLAP') {
    const ranges = str(body.overlapRanges);
    if (ranges) {
      return t('apiError.budgetMainOverlap', { ranges });
    }
    return t('apiError.budgetMainOverlap');
  }

  if (code === 'SUB_BUDGET_OUT_OF_MAIN_RANGE') {
    return t('apiError.subBudgetOutOfMainRange');
  }

  if (code === 'MAIN_BUDGET_LOCKED_BY_SUBMITTED_SETTLEMENT') {
    return t('apiError.mainBudgetLockedBySubmittedSettlement');
  }

  if (code === 'RECEIPT_DELETE_STATUS_NOT_ALLOWED') {
    return t('apiError.receiptDeleteStatusNotAllowed');
  }

  if (code === 'RECEIPT_UPDATE_STATUS_NOT_ALLOWED') {
    return t('apiError.receiptUpdateStatusNotAllowed');
  }

  if (code === 'SETTLEMENT_ALREADY_UNLOCKED') {
    return t('apiError.settlementAlreadyUnlocked');
  }

  return null;
};

/** Localized message when available; otherwise the API error string or a generic fallback. */
export const resolveApiErrorMessage = (
  err: unknown,
  t: Translate,
  fallbackKey = 'auth.errorGeneric',
): string => mapApiClientErrorToMessage(err, t) ?? (err instanceof Error ? err.message : t(fallbackKey));
